import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiX, FiImage, FiShare2, FiVideo, FiSearch, FiBell } from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';
import { useToast } from '../Context/ToastContext';
import { useNotifications } from '../Context/NotificationContext';
import { useLanguage } from '../Context/LanguageContext';
import './ToastNotification.css';

// Icon mapping for notification types
const NOTIFICATION_ICONS = {
  image_generation_complete: FiImage,
  social_media_complete: FiShare2,
  video_generation_complete: FiVideo,
  seo_analysis_complete: FiSearch,
};

const ToastNotification = () => {
  const { toasts, removeToast } = useToast();
  const { markAsRead } = useNotifications();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [exitingToasts, setExitingToasts] = useState({});

  // Handle toast close with animation
  const handleClose = useCallback((e, toastId) => {
    e.stopPropagation();
    setExitingToasts(prev => ({ ...prev, [toastId]: true }));
    setTimeout(() => {
      removeToast(toastId);
      setExitingToasts(prev => {
        const newState = { ...prev };
        delete newState[toastId];
        return newState;
      });
    }, 280); // Match animation duration
  }, [removeToast]);

  // Handle toast click - navigate and mark as read
  const handleToastClick = useCallback((toast) => {
    // Mark notification as read if it has a notification_id
    if (toast.metadata?.notification_id) {
      markAsRead(toast.metadata.notification_id);
    }

    // Navigate to the related content
    if (toast.metadata?.navigation_path) {
      navigate(toast.metadata.navigation_path);
    }

    // Remove the toast
    setExitingToasts(prev => ({ ...prev, [toast.id]: true }));
    setTimeout(() => {
      removeToast(toast.id);
      setExitingToasts(prev => {
        const newState = { ...prev };
        delete newState[toast.id];
        return newState;
      });
    }, 280);
  }, [markAsRead, navigate, removeToast]);

  // Get icon for notification type
  const getIcon = (toast) => {
    // If toast has an image URL, show thumbnail
    if (toast.metadata?.image_url) {
      return (
        <img
          src={toast.metadata.image_url}
          alt=""
          className="toast-thumbnail"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      );
    }

    const IconComponent = NOTIFICATION_ICONS[toast.type] || FiBell;
    const iconClass = NOTIFICATION_ICONS[toast.type] ? toast.type : 'default';
    return (
      <div className={`toast-icon ${iconClass}`}>
        <IconComponent />
      </div>
    );
  };

  // Format relative time with locale support
  const formatTime = (date) => {
    try {
      const locale = lang === 'tr' ? tr : enUS;
      return formatDistanceToNow(date, {
        addSuffix: true,
        locale
      });
    } catch (error) {
      return '';
    }
  };

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`toast-item ${exitingToasts[toast.id] ? 'exiting' : ''}`}
          onClick={() => handleToastClick(toast)}
        >
          {getIcon(toast)}

          <div className="toast-content">
            <p className="toast-title">{toast.title}</p>
            {toast.message && <p className="toast-message">{toast.message}</p>}
            <p className="toast-time">{formatTime(toast.createdAt)}</p>
          </div>

          <button
            className="toast-close"
            onClick={(e) => handleClose(e, toast.id)}
            aria-label={t('notifications.dismiss') || 'Dismiss'}
          >
            <FiX />
          </button>

          <div
            className="toast-progress"
            style={{
              animation: `toastProgress ${toast.duration || 8000}ms linear forwards`
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default ToastNotification;
